import React, { useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { buildTableQrUrl, openPrintWindow, PrintableQrItem } from '../../utils/qrUtils';

interface TableLike {
  id: string;
  name: string;
}

interface BulkQRCodePrinterProps {
  storeId: string;
  tables: TableLike[];
  /** true の間だけ隠し canvas をレンダーし、描画完了後に一括印刷する */
  trigger: boolean;
  /** 印刷処理を呼び出した後（または対象0件のとき）に呼ばれる */
  onDone: () => void;
}

/**
 * 全テーブル分の sitting URL QR をオフスクリーンに描画し、まとめて印刷するための
 * ヘッドレスコンポーネント。trigger が true の間だけ隠し canvas を生成する。
 */
const BulkQRCodePrinter: React.FC<BulkQRCodePrinterProps> = ({
  storeId,
  tables,
  trigger,
  onDone,
}) => {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    if (!trigger) return;

    if (tables.length === 0) {
      onDone();
      return;
    }

    // qrcode.react は自身の effect で canvas を描画するため、
    // 次フレームまで待ってから dataURL を収集する。
    const rafId = requestAnimationFrame(() => {
      const items: PrintableQrItem[] = tables
        .map((table, index) => {
          const canvas = canvasRefs.current[index];
          if (!canvas) return null;
          return { name: table.name, dataUrl: canvas.toDataURL('image/png') };
        })
        .filter((item): item is PrintableQrItem => item !== null);

      openPrintWindow(items);
      onDone();
    });

    return () => cancelAnimationFrame(rafId);
  }, [trigger, tables, onDone]);

  if (!trigger) return null;

  return (
    <div style={{ position: 'absolute', left: '-9999px', top: 0 }} aria-hidden>
      {tables.map((table, index) => (
        <QRCodeCanvas
          key={table.id}
          value={buildTableQrUrl(storeId, table.id)}
          size={240}
          marginSize={2}
          level="M"
          ref={(el) => {
            canvasRefs.current[index] = el;
          }}
        />
      ))}
    </div>
  );
};

export default BulkQRCodePrinter;
